import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "../lib/content";
import { handleAnchor, setScrollLock } from "../lib/site";
import { Wordmark } from "./JLMark";

const useActiveSection = () => {
  const [active, setActive] = useState("inicio");
  useEffect(() => {
    const els = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return active;
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setScrollLock(open);
    return () => setScrollLock(false);
  }, [open]);

  const go = (e, id) => {
    setOpen(false);
    setScrollLock(false);
    handleAnchor(e, id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "border-b border-white/10 bg-[#080B12]/75 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <a href="#inicio" onClick={(e) => go(e, "inicio")} data-testid="header-logo-link" aria-label="JL Nexus, voltar ao início">
          <Wordmark />
        </a>
        <nav className="hidden items-center gap-1 xl:flex" aria-label="Navegação principal">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              onClick={(e) => go(e, n.id)}
              data-testid={`nav-link-${n.id}`}
              className={`relative rounded-full px-3.5 py-2 text-sm transition-colors duration-300 ${active === n.id ? "text-white" : "text-slate-300 hover:text-white"}`}
            >
              {active === n.id && (
                <motion.span layoutId="nav-pill" className="absolute inset-0 -z-10 rounded-full border border-[#087BFF]/40 bg-[#087BFF]/10" transition={{ type: "spring", stiffness: 380, damping: 32 }} />
              )}
              {n.id === "faq" ? "FAQ" : n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="#contato"
            onClick={(e) => go(e, "contato")}
            data-testid="header-cta-button"
            className="btn-shine group hidden items-center gap-2 rounded-full bg-[#087BFF] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_rgba(8,123,255,0.45)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_36px_rgba(0,200,255,0.6)] sm:inline-flex"
          >
            Solicitar orçamento
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            data-testid="mobile-menu-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav"
            data-testid="mobile-menu"
            aria-label="Menu móvel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "calc(100svh - 72px)" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-y-auto bg-[#080B12]/95 backdrop-blur-xl xl:hidden"
          >
            <ul className="flex flex-col px-6 pb-10 pt-4">
              {NAV.map((n, i) => (
                <motion.li key={n.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.05 }}>
                  <a
                    href={`#${n.id}`}
                    onClick={(e) => go(e, n.id)}
                    data-testid={`mobile-nav-link-${n.id}`}
                    className="flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl text-white"
                  >
                    {n.label}
                    <span className="font-mono-jl text-xs text-[#00C8FF]">0{i + 1}</span>
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className="px-6">
              <a href="#contato" onClick={(e) => go(e, "contato")} data-testid="mobile-menu-cta" className="flex h-14 items-center justify-center rounded-full bg-[#087BFF] font-semibold text-white">
                Solicitar orçamento
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
